import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/api";
import { useAuth } from "@/auth";
import { useLang } from "@/i18n";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";
import {
  Users, Home, HomeIcon, FileText, FileWarning, Tractor, LandPlot, Droplets, Zap, ZapOff, RotateCcw, Lightbulb, UserRound, Baby, PersonStanding,
} from "lucide-react";

const COLORS = ["#2E7D32", "#0277BD", "#D32F2F", "#D97706", "#455A64", "#558B2F", "#00838F", "#8D6E63", "#C62828", "#1565C0"];
const TOOLTIP_STYLE = { background: "#fff", border: "1px solid #E2E8F0", borderRadius: 6, fontSize: 12 };

const KPI_DEFS = [
  { key: "total_families", label: "kpi_total_families", icon: Users, accent: "text-graphite", bg: "bg-slate-100", to: "/families" },
  { key: "total_population", label: "kpi_total_population", icon: UserRound, accent: "text-graphite", bg: "bg-slate-100", to: "/families" },
  { key: "own_houses", label: "kpi_own_houses", icon: Home, accent: "text-agri", bg: "bg-agri-light", to: "/families?own_house=yes" },
  { key: "without_own_house", label: "kpi_without_own_house", icon: HomeIcon, accent: "text-brand-red", bg: "bg-red-50", to: "/families?own_house=no" },
  { key: "pattas_issued", label: "kpi_pattas_issued", icon: FileText, accent: "text-water", bg: "bg-water-light", to: "/families?patta_issued=yes" },
  { key: "requiring_patta", label: "kpi_requiring_patta", icon: FileWarning, accent: "text-amber-700", bg: "bg-amber-50", to: "/families?needs_patta=yes" },
  { key: "land_owners", label: "kpi_land_owners", icon: Tractor, accent: "text-agri", bg: "bg-agri-light", to: "/families?owns_land=yes" },
  { key: "landless", label: "kpi_landless", icon: LandPlot, accent: "text-brand-red", bg: "bg-red-50", to: "/families?owns_land=no" },
  { key: "total_land_acres", label: "kpi_total_land", icon: LandPlot, accent: "text-agri", bg: "bg-agri-light", to: "/families?owns_land=yes" },
  { key: "gov_borewells", label: "kpi_gov_borewells", icon: Droplets, accent: "text-water", bg: "bg-water-light", to: "/families?borewell_type=Government" },
  { key: "private_borewells", label: "kpi_private_borewells", icon: Droplets, accent: "text-graphite", bg: "bg-slate-100", to: "/families?borewell_type=Private" },
  { key: "bw_with_electricity", label: "kpi_bw_with_electricity", icon: Zap, accent: "text-agri", bg: "bg-agri-light", to: "/families?bw_electricity=yes" },
  { key: "bw_without_electricity", label: "kpi_bw_without_electricity", icon: ZapOff, accent: "text-brand-red", bg: "bg-red-50", to: "/families?bw_electricity=no" },
  { key: "requiring_borewell", label: "kpi_requiring_borewell", icon: Droplets, accent: "text-amber-700", bg: "bg-amber-50", to: "/families?needs_borewell=yes" },
];

function ChartCard({ title, children, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid={testid}>
      <h3 className="text-sm font-heading font-semibold text-graphite mb-3">{title}</h3>
      {children}
    </div>
  );
}

export default function Dashboard() {
  const { t } = useLang();
  const { scope, demoMode } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [resetting, setResetting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/dashboard", { params: { scope } });
      setData(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  if (!data) {
    return (
      <div className="flex items-center justify-center py-32" data-testid="dashboard-loading">
        <div className="h-10 w-10 rounded-full border-2 border-brand-red border-t-transparent animate-spin" />
      </div>
    );
  }

  const { kpis, charts, insights } = data;

  const resetDemo = async () => {
    setResetting(true);
    try {
      await api.post("/demo/reset");
      toast.success(t("reset_done"));
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl tracking-tight font-semibold text-graphite" data-testid="dashboard-title">{t("dashboard_title")}</h1>
          <p className="text-sm text-slate-500 mt-1">{t("dashboard_subtitle", { village: data.village })}</p>
        </div>
        {demoMode && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button data-testid="reset-demo-btn" variant="outline" className="border-brand-red text-brand-red hover:bg-red-50 transition-colors duration-200">
                <RotateCcw className="h-4 w-4 mr-2" />
                {t("reset_demo_data")}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t("reset_confirm_title")}</AlertDialogTitle>
                <AlertDialogDescription>{t("reset_confirm_desc")}</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="reset-cancel-btn">{t("cancel")}</AlertDialogCancel>
                <AlertDialogAction
                  data-testid="reset-confirm-btn"
                  className="bg-brand-red hover:bg-brand-reddark text-white"
                  onClick={resetDemo}
                  disabled={resetting}
                >
                  {t("confirm")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>

      {kpis.total_families === 0 && (
        <div className="bg-white border border-dashed border-slate-300 rounded-lg p-10 text-center text-sm text-slate-500" data-testid="dashboard-empty-state">
          {t("empty_dashboard")}
        </div>
      )}

      {insights.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="village-insights">
          <div className="flex items-center gap-2 mb-4">
            <Lightbulb className="h-4 w-4 text-amber-600" />
            <h2 className="text-lg font-heading font-medium text-graphite">{t("village_insights")}</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {insights.map((ins, i) => (
              <button
                key={ins.key}
                data-testid={`insight-${ins.key}`}
                onClick={() => navigate(`/families?${new URLSearchParams(ins.filters).toString()}`)}
                className="text-left bg-amber-50/60 border border-amber-200 rounded-md px-4 py-3 text-sm text-amber-900 hover:bg-amber-50 transition-colors duration-200 animate-fade-up"
                style={{ animationDelay: `${i * 60}ms` }}
              >
                {t(`insight_${ins.key}`, { count: ins.count, crop: ins.crop })}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4" data-testid="kpi-grid">
        {KPI_DEFS.map((k, i) => (
          <button
            key={k.key}
            data-testid={`kpi-${k.key.replace(/_/g, "-")}`}
            onClick={() => navigate(k.to)}
            className="kpi-card bg-white border border-slate-200 rounded-lg p-4 text-left animate-fade-up"
            style={{ animationDelay: `${i * 40}ms` }}
          >
            <div className={`h-8 w-8 rounded-md flex items-center justify-center mb-3 ${k.bg}`}>
              <k.icon className={`h-4 w-4 ${k.accent}`} />
            </div>
            <div className="text-2xl font-heading font-bold text-graphite leading-none mb-1.5">{kpis[k.key]}</div>
            <div className="text-[11px] text-slate-500 leading-snug">{t(k.label)}</div>
          </button>
        ))}
        <div className="bg-white border border-slate-200 rounded-lg p-4 animate-fade-up" style={{ animationDelay: "560ms" }} data-testid="kpi-demographics">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">{t("demographics_title")}</div>
          <div className="space-y-1.5 text-xs text-slate-600">
            <div className="flex items-center justify-between"><span className="flex items-center gap-1.5"><Baby className="h-3.5 w-3.5 text-water" />{t("kpi_children")}</span><b>{kpis.children}</b></div>
            <div className="flex items-center justify-between"><span className="flex items-center gap-1.5"><PersonStanding className="h-3.5 w-3.5 text-agri" />{t("kpi_adults")}</span><b>{kpis.adults}</b></div>
            <div className="flex items-center justify-between"><span className="flex items-center gap-1.5"><UserRound className="h-3.5 w-3.5 text-amber-700" />{t("kpi_senior_citizens")}</span><b>{kpis.senior_citizens}</b></div>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        <ChartCard title={t("chart_house_ownership")} testid="chart-house-ownership">
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={charts.house_ownership} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75}
                onClick={(d) => navigate(d.name === "Own House" ? "/families?own_house=yes" : "/families?own_house=no")} cursor="pointer" label>
                {charts.house_ownership.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title={t("chart_patta_status")} testid="chart-patta-status">
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={charts.patta_status} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} cursor="pointer" label
                onClick={(d) => {
                  if (d.name === "Patta Issued") navigate("/families?patta_issued=yes");
                  else if (d.name === "Requires Patta") navigate("/families?needs_patta=yes");
                  else navigate("/families?own_house=yes");
                }}>
                {charts.patta_status.map((_, i) => <Cell key={i} fill={[COLORS[1], COLORS[3], COLORS[0]][i % 3]} />)}
              </Pie>
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title={t("chart_land_ownership")} testid="chart-land-ownership">
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={charts.land_ownership} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} cursor="pointer" label
                onClick={(d) => navigate(d.name === "Land Owners" ? "/families?owns_land=yes" : "/families?owns_land=no")}>
                {charts.land_ownership.map((_, i) => <Cell key={i} fill={[COLORS[0], COLORS[2]][i % 2]} />)}
              </Pie>
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title={t("chart_crop_distribution")} testid="chart-crop-distribution">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={charts.crop_distribution} layout="vertical" margin={{ left: 30 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" />
              <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
              <YAxis type="category" dataKey="crop" tick={{ fontSize: 11 }} width={80} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Bar dataKey="farmers" fill="#2E7D32" radius={[0, 4, 4, 0]} cursor="pointer"
                onClick={(d) => navigate(`/families?crop=${encodeURIComponent(d.crop)}`)} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title={t("chart_borewell_status")} testid="chart-borewell-status">
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={charts.borewell_status} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} cursor="pointer" label
                onClick={(d) => navigate(`/families?borewell_status=${encodeURIComponent(d.name)}`)}>
                {charts.borewell_status.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title={t("chart_borewell_electricity")} testid="chart-borewell-electricity">
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={charts.borewell_electricity} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} cursor="pointer" label
                onClick={(d) => navigate(d.name === "With Electricity" ? "/families?bw_electricity=yes" : "/families?bw_electricity=no")}>
                {charts.borewell_electricity.map((_, i) => <Cell key={i} fill={[COLORS[1], COLORS[2]][i % 2]} />)}
              </Pie>
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title={t("chart_education")} testid="chart-education">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={charts.education} layout="vertical" margin={{ left: 30 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" />
              <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={95} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Bar dataKey="value" fill="#0277BD" radius={[0, 4, 4, 0]} cursor="pointer"
                onClick={(d) => navigate(`/families?education=${encodeURIComponent(d.name)}`)} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title={t("chart_profession")} testid="chart-profession">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={charts.profession} layout="vertical" margin={{ left: 30 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" />
              <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={95} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Bar dataKey="value" fill="#D97706" radius={[0, 4, 4, 0]} cursor="pointer"
                onClick={(d) => navigate(`/families?profession=${encodeURIComponent(d.name)}`)} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title={t("chart_caste")} testid="chart-caste">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={charts.caste} layout="vertical" margin={{ left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" />
              <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
              <YAxis type="category" dataKey="category" tick={{ fontSize: 11 }} width={50} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Bar dataKey="people" fill="#455A64" radius={[0, 4, 4, 0]} cursor="pointer"
                onClick={(d) => navigate(`/families?caste=${encodeURIComponent(d.category)}`)} />
            </BarChart>
          </ResponsiveContainer>
          <p className="text-[10px] text-slate-400 mt-2">{t("caste_disclaimer")}</p>
        </ChartCard>

        <ChartCard title={t("chart_gender")} testid="chart-gender">
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={charts.gender} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} cursor="pointer" label
                onClick={(d) => navigate(`/families?gender=${encodeURIComponent(d.name)}`)}>
                {charts.gender.map((_, i) => <Cell key={i} fill={[COLORS[1], COLORS[2], COLORS[4]][i % 3]} />)}
              </Pie>
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title={t("chart_age_groups")} testid="chart-age-groups">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={charts.age_groups}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Bar dataKey="value" fill="#00838F" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title={t("chart_marital")} testid="chart-marital">
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={charts.marital_status} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} label>
                {charts.marital_status.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
}
